<template>
  <div>
    <!-- Enhanced gradient controls with better UI inspired by shadow module -->
    <div class="d-flex align-items-center mt-2 mb-3 flex-wrap">
      <b-button
        v-for="(preset, key) in colorPresets"
        :key="key"
        class="text-capitalize color-preset mb-2"
        :class="[`preset-${key}`]"
        :variant="`${key === activePreset ? '' : 'outline-'}primary`"
        size="sm"
        @click="useColorPreset(key)"
      >
        {{ key }}
      </b-button>
    </div>

    <!-- Improved text color type selection with better organization -->
    <b-form-group
      label="Text Color Type"
      label-cols="4"
    >
      <b-form-radio-group
        v-model="colorType"
        :options="colorTypeOptions"
        buttons
        button-variant="outline-primary"
        size="sm"
        @change="onColorTypeChange"
      />
    </b-form-group>

    <!-- Show angle control only for gradients -->
    <b-form-group
      v-show="editor.type === 'linear' || editor.type === 'radial'"
      label="Gradient Angle"
      label-cols="4"
    >
      <b-input-group>
        <b-input
          v-model="editor.angle"
          max="360"
          min="0"
          number
          type="range"
          @input="redraw"
        />
        <template #append>
          <b-input
            v-model="editor.angle"
            max="360"
            min="0"
            number
            type="number"
            @input="redraw"
          />
          <b-input-group-text>°</b-input-group-text>
        </template>
      </b-input-group>
      <!-- Added visual angle indicator -->
      <div class="angle-indicator mt-2">
        <div 
          class="angle-arrow"
          :style="{ transform: `rotate(${editor.angle}deg)` }"
        >
          →
        </div>
      </div>
    </b-form-group>

    <!-- Enhanced transparency control with better labeling -->
    <b-form-group
      label="Text Opacity"
      label-cols="4"
    >
      <b-input-group>
        <b-input
          v-model="editor.transparency"
          max="100"
          min="0"
          number
          type="range"
          @input="redraw"
        />
        <template #append>
          <b-input
            v-model="editor.transparency"
            max="100"
            min="0"
            number
            type="number"
            @input="redraw"
          />
          <b-input-group-text>%</b-input-group-text>
        </template>
      </b-input-group>
    </b-form-group>

    <!-- Enhanced color stops control with better organization -->
    <b-form-group
      v-show="editor.type === 'linear' || editor.type === 'radial'"
      label="Color Stops"
      label-cols="4"
    >
      <b-input-group>
        <b-input
          v-model="colorsNum"
          max="5"
          min="2"
          type="range"
          @input="colorsNumChange"
        />
        <template #append>
          <b-input
            v-model="colorsNum"
            max="5"
            min="2"
            number
            type="number"
            @input="colorsNumChange"
          />
          <b-input-group-text>stops</b-input-group-text>
        </template>
      </b-input-group>
    </b-form-group>

    <!-- Improved color picker layout -->
    <b-form-group 
      label="Colors"
      label-cols="4"
    >
      <template v-if="colorType === 'solid'">
        <div class="d-flex align-items-center mb-2">
          <b-input
            v-model="editor.colors[0]"
            class="color-fill-item mr-2"
            debounce="50"
            type="color"
            @input="redraw"
          />
          <b-input
            v-model="editor.colors[0]"
            class="flex-grow-1"
            type="text"
            placeholder="Enter hex color (e.g., #FF5733)"
            @change="onInputColor($event, 0)"
          />
        </div>
      </template>
      
      <!-- Enhanced gradient color stops with real-time preview -->
      <ColorStop
        v-if="editor.type === 'linear' || editor.type === 'radial'"
        :active-color="activeColor"
        :editor="editor"
        :show-color-picker="showColorPicker"
        tabindex="0"
        @colorStepsInput="updateStepOfColor"
        @colorStopClicked="setActiveColor"
        @colorUpdated="updateColor"
      />
    </b-form-group>

    <!-- Added real-time preview section -->
    <b-form-group
      label="Live Preview"
      label-cols="4"
    >
      <div 
        class="text-preview"
        :style="getPreviewStyle()"
      >
        Sample Gradient Text
      </div>
    </b-form-group>

    <!-- Added CSS output for easy copying -->
    <b-form-group
      label="CSS Output"
      label-cols="4"
    >
      <div class="css-output-container">
        <b-textarea
          ref="cssOutput"
          :value="getCSSOutput()"
          readonly
          rows="4"
          class="css-output"
        />
        <b-button
          size="sm"
          variant="outline-secondary"
          class="copy-btn"
          @click="copyCSSToClipboard"
        >
          <b-icon icon="clipboard" />
        </b-button>
      </div>
    </b-form-group>
  </div>
</template>

<script>

import {deepCopy} from "@frontend/utils";
import {BackgroundMakerV2} from "@frontend/BackgroundMaker";
import ColorStop from "@frontend/components/modules/layouts-page/EditorControl/components/ColorStop.vue";
import _ from 'lodash';
import toastr from 'toastr';
import { fabric } from 'fabric';
import { drawTextWithGradient, enhanceTextWithGradient } from "@/common/preview/draw-multi-line-text";
import { nextTick } from 'vue';

export const DEFAULT_NO_COLOR = {
  "type": "solid",
  "aspectRatio": null,
  "angle": 0,
  "colors": [
    "#000000"
  ],
  "rounding": 0,
  "transparency": 0,
  "edges": {
    "topLeft": false,
    "topRight": false,
    "bottomLeft": false,
    "bottomRight": false
  },
  "colorSteps": [
    50,
    50,
    50,
    50,
    50
  ],
  "blur": 0
};
const MAX_DIMENSION = 300
const MAX_DIMENSION_W_UNIT = `${MAX_DIMENSION}px`
export default {
  name: 'TextColorEditor',
  components: {
    ColorStop,
  },
  props: {
    target: {
      type: Object,
      default: () => null,
      required: true
    },

    width: {
      type: Number,
      default: 1000,
    },
    height: {
      type: Number,
      default: 1000,
    },
  },
  data: () => ({
    localTarget: null,
    isUpdating: false,
    canvasStyle: {
      width: MAX_DIMENSION_W_UNIT,
      height: MAX_DIMENSION_W_UNIT,
    },
    canvasWrapperStyle: {
      width: MAX_DIMENSION_W_UNIT,
      height: MAX_DIMENSION_W_UNIT,
    },
    showColorPicker: false,
    editor: {
      type: 'linear',
      angle: 0,
      colors: ['#000000'],
      colorSteps: [50],
      transparency: 0,
      width: 1000,
      height: 1000
    },
    colorsNum: 1,
    activeColor: '#FFFFFF',
    activeColorIndex: 0,
    trimTransparentArea: false,
    maxRounding: 0,
    activeShape: 'none',
    shapes: {},
    colorPresets: {
      solid: {
        type: 'solid',
        colors: ['#000000'],
        transparency: 0
      },
      sunset: {
        type: 'linear',
        angle: 315,
        colors: ['#ff6b6b', '#feca57', '#ff9ff3'],
        colorSteps: [0, 50, 100],
        transparency: 0
      },
      ocean: {
        type: 'linear',
        angle: 0,
        colors: ['#667eea', '#764ba2', '#f093fb'],
        colorSteps: [0, 50, 100],
        transparency: 0
      },
      fire: {
        type: 'radial',
        colors: ['#ff9a9e', '#fecfef', '#fecfef'],
        colorSteps: [0, 70, 100],
        transparency: 0
      },
      neon: {
        type: 'linear',
        angle: 45,
        colors: ['#00f5ff', '#ff00ff', '#ffff00'],
        colorSteps: [0, 50, 100],
        transparency: 0
      }
    },
    colorType: 'solid',
    colorTypeOptions: [
      { text: 'Solid', value: 'solid' },
      { text: 'Linear', value: 'linear' },
      { text: 'Radial', value: 'radial' }
    ],
  }),
  computed: {
    fileName: {
      get() {
        return this.editor.fileName
      },
      set(val) {
        this.editor.fileName = val
      },
    },
    selectedCorners: {
      get() {
        return _.keys(_.pickBy(this.editor.edges))
      },
      set(val) {
        this.$set(this.editor, 'edges', _.mapValues(this.editor.edges, (v, k) => _.includes(val, k)))
      },
    },
    disabledRounding() {
      return this.activeShape === 'circle'
    },
    isGradientSelected() {
      return !(this.editor.type === 'linear' || this.editor.type === 'radial');
    },
    activePreset() {
      return _.findKey(this.colorPresets, preset => {
        if (!preset || !this.editor) return false;
        return preset.type === this.editor.type;
      }) || 'custom';
    }
  },
  watch: {
    target: {
      immediate: true,
      handler(newTarget) {
        if (newTarget) {
          this.init();
        }
      }
    },
    'editor.colors': {
      handler() {
        this.$forceUpdate(); // Force preview update
      },
      deep: true
    },
    'editor.colorSteps': {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    },
    'editor.angle'() {
      this.$forceUpdate();
    },
    'editor.transparency'() {
      this.$forceUpdate();
    },
  },
  async mounted() {
    this.init()
  },
  methods: {
    init() {
      if (this.isUpdating) {
        return;
      }

      if (!this.target) {
        return;
      }

      // Create a local copy of the target
      this.localTarget = { ...this.target };

      // Check if the target has existing gradient settings
      const hasGradientSettings = this.localTarget.gradientSettings || 
                                 (this.localTarget.editor && this.localTarget.editor.type);

      if (hasGradientSettings) {
        // Use existing gradient settings from the target
        const gradientSettings = this.localTarget.gradientSettings || this.localTarget.editor;
        
        // Update the editor with the restored settings
        this.editor.type = gradientSettings.type || 'solid';
        this.editor.angle = gradientSettings.angle || 0;
        this.editor.colors = gradientSettings.colors ? [...gradientSettings.colors] : ['#000000'];
        this.editor.colorSteps = gradientSettings.colorSteps ? [...gradientSettings.colorSteps] : [50];
        this.editor.transparency = gradientSettings.transparency || 0;
        this.editor.width = this.localTarget.width || this.width || 1000;
        this.editor.height = this.localTarget.height || this.height || 1000;
        
        // Update UI properties
        this.colorType = this.editor.type;
        this.colorsNum = this.editor.colors.length;
        this.activeColor = this.editor.colors[this.activeColorIndex] || '#000000';
        
        // Also store in editor for persistence
        if (this.localTarget.editor) {
          this.localTarget.editor = { ...this.localTarget.editor, ...this.editor };
        } else {
          this.localTarget.editor = { ...this.editor };
        }
      } else if (!this.localTarget.editor) {
        // Use default settings if no existing gradient
        this.localTarget.editor = { ...DEFAULT_NO_COLOR };
        this.editor = { ...DEFAULT_NO_COLOR };
        this.colorType = this.editor.type;
        this.colorsNum = this.editor.colors.length;
      } else {
        // Use existing editor settings
        this.editor = { ...this.localTarget.editor };
        this.colorType = this.editor.type;
        this.colorsNum = this.editor.colors.length;
      }

      const targetWidth = this.localTarget.width || this.width || 1000;
      const targetHeight = this.localTarget.height || this.height || 1000;
      
      this.maxRounding = Math.max(targetWidth, targetHeight) / 2;
      
      if (!this.editor.colors || !Array.isArray(this.editor.colors)) {
        this.editor.colors = ['#000000'];
      }
      if (!this.editor.colorSteps || !Array.isArray(this.editor.colorSteps)) {
        this.editor.colorSteps = [50];
      }
      
      this.editor.width = targetWidth;
      this.editor.height = targetHeight;
      
      this.colorsNum = this.editor.colors.length;
      this.activeColor = this.editor.colors[this.activeColorIndex] || '#000000';
      this.colorType = this.editor.type;

      this.updateCanvasDimensions();

      // Ensure we have at least 2 colors for gradients
      if ((this.editor.type === 'linear' || this.editor.type === 'radial') && this.editor.colors.length < 2) {
          this.editor.colors.push('#ffffff');
          this.colorsNum = 2;
      }
      this.$nextTick(() => {
          this.$forceUpdate();
      });
    },
    async redraw(avoidSettingsUpdate = true) {
      if (this.isUpdating) {
        return;
      }
      
      this.isUpdating = true;

      await new Promise(resolve => setTimeout(resolve, 50));
      
      const gradientSettings = {
        type: this.editor.type,
        angle: this.editor.angle,
        colors: [...this.editor.colors],
        colorSteps: this.editor.colorSteps,
        transparency: this.editor.transparency
      };
      
      if (this.localTarget) {
        this.localTarget.gradientSettings = { ...gradientSettings };
        
        // Also update editor if it exists
        if (this.localTarget.editor) {
          this.localTarget.editor = { ...this.localTarget.editor, ...gradientSettings };
        }
      }

      this.$emit('gradientApplied', gradientSettings);
      
      if (avoidSettingsUpdate && this.localTarget) {
        if (this.localTarget.updateSettings && typeof this.localTarget.updateSettings === 'function') {
          this.localTarget.updateSettings(deepCopy(this.editor.getSettings ? this.editor.getSettings() : this.editor));
        }
      }

      this.$nextTick(() => {
        setTimeout(() => {
          this.isUpdating = false;
        }, 100);
      });
    },

    useColorPreset(presetKey) {
      const preset = this.colorPresets[presetKey];
      if (!preset) return;
      
      Object.assign(this.editor, preset);
      this.colorsNum = this.editor.colors.length;
      this.colorType = preset.type;
      this.redraw();
    },

    onInputColor(inputColor, index) {
      if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(inputColor)) {
        this.$set(this.editor.colors, index, inputColor);
        this.redraw();
      } else {
        toastr.error("Invalid color format. Please use hex color codes.");
      }
    },

    colorsNumChange() {
      const minColors = (this.editor.type === 'linear' || this.editor.type === 'radial') ? 2 : 1;
      if (this.colorsNum < minColors) {
        this.colorsNum = minColors;
      }

      const delta = this.colorsNum - this.editor.colors.length;

      if (delta > 0) {
        for (let i = 0; i < delta; i++) {
          this.editor.colors.push(BackgroundMakerV2.randomColor());
        }
      } else {
        this.editor.colors.splice(delta);
      }

      for (let i = 0; i < this.editor.colors.length; i++) {
        this.editor.colorSteps[i] = (i / (this.editor.colors.length - 1)) * 100;
      }
      this.redraw();
    },

    updateStepOfColor({index, value}) {
      this.editor.colorSteps[index] = value;
      this.$forceUpdate();
      this.redraw();
    },

    setActiveColor({color, index}) {
      this.activeColor = color
      this.activeColorIndex = index
      this.$nextTick(() => {
        this.showColorPicker = true
      })
    },

    updateColor({color,index}) {
      this.$set(this.editor.colors, index, color);
      this.redraw();
    },

    async updateCanvasDimensions() {
      const aspectRatio = this.editor.width / this.editor.height
      
      if (aspectRatio >= 1) {
        this.canvasStyle.width = MAX_DIMENSION_W_UNIT
        this.canvasStyle.height = `${MAX_DIMENSION / aspectRatio}px`
      } else {
        this.canvasStyle.height = MAX_DIMENSION_W_UNIT
        this.canvasStyle.width = `${MAX_DIMENSION * aspectRatio}px`
      }

      this.maxRounding = Math.floor(Math.max(this.editor.width, this.editor.height) / 2)
      
      if (this.activeShape === 'circle') {
        this.editor.rounding = this.maxRounding
      }

      this.redraw(false)
    },
    hideColorPicker ({e, colorPickerElement}) {
      if (!colorPickerElement.contains(e.relatedTarget)) {
        this.showColorPicker = false
      }
    },
    disableGradients() {
      if (this.editor.solidColor) {
        this.editor.linearGradient = false;
        this.editor.radialGradient = false;
      }
    },
    getPreviewStyle() {
      if (this.editor.type === 'solid') {
        const color = (this.editor.colors && this.editor.colors[0]) || '#000000';
        const opacity = (100 - this.editor.transparency) / 100;
        return {
          color: this.addOpacityToColor(color, opacity),
          fontSize: '28px',
          fontWeight: 'bold',
          padding: '15px',
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif'
        };
      }

      const gradientType = this.editor.type;
      const angle = this.editor.angle + 90 || 0;
      const colors = (this.editor.colors || []).map((color, index) => {
        const stop = this.editor.colorSteps && this.editor.colorSteps[index] !== undefined ? 
          this.editor.colorSteps[index] : 
          (index / (this.editor.colors.length - 1)) * 100;
        const opacity = (100 - this.editor.transparency) / 100;
        return `${this.addOpacityToColor(color, opacity)} ${stop}%`;
      }).join(', ');

      const gradient = gradientType === 'linear' ? 
        `linear-gradient(${angle}deg, ${colors})` :
        `radial-gradient(circle, ${colors})`;

      return {
        background: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontSize: '28px',
        fontWeight: 'bold',
        padding: '15px',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.2'
      };
    },
    getCSSOutput() {
      if (this.editor.type === 'solid') {
        const color = (this.editor.colors && this.editor.colors[0]) || '#000000';
        const opacity = (100 - this.editor.transparency) / 100;
        return `color: ${this.addOpacityToColor(color, opacity)};`;
      }

      const gradientType = this.editor.type;
      const angle = this.editor.angle || 0;
      const colors = (this.editor.colors || []).map((color, index) => {
        const stop = this.editor.colorSteps && this.editor.colorSteps[index] !== undefined ? 
          this.editor.colorSteps[index] : 
          (index / (this.editor.colors.length - 1)) * 100;
        const opacity = (100 - this.editor.transparency) / 100;
        return `${this.addOpacityToColor(color, opacity)} ${stop}%`;
      }).join(', ');

      const gradient = gradientType === 'linear' ? 
        `linear-gradient(${angle}deg, ${colors})` :
        `radial-gradient(circle, ${colors})`;

      return `background: ${gradient};
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;
background-clip: text;
display: inline-block;`;
    },
    copyCSSToClipboard() {
      const cssText = this.getCSSOutput();
      navigator.clipboard.writeText(cssText).then(() => {
        this.$bvToast.toast('CSS copied to clipboard!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      }).catch(() => {
        this.$refs.cssOutput.select();
        document.execCommand('copy');
        this.$bvToast.toast('CSS copied to clipboard!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      });
    },
    onColorTypeChange(newType) {
      this.editor.type = newType;
      
      if (newType !== 'solid' && this.editor.colors.length < 2) {
        this.editor.colors.push('#ffffff');
        this.colorsNum = 2;
      }
      
      this.redraw();
    },
    addOpacityToColor(color, opacity) {
      if (color.startsWith('#')) {
        const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
        return color + alpha;
      }
      return color;
    }
  }
}
</script>

<style lang="scss" scoped>
.canvas-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-stop-range {
  margin: 15px 0 0 0;
}

.color-fill-item {
  width: 50px;
  height: 38px;
  border-radius: 6px;
  border: 2px solid #dee2e6;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:hover {
    border-color: #007bff;
  }
}

.color-preset {
  flex: 0 0 18%;
  margin-right: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  padding: 8px 12px;
  
  &:nth-child(5n) {
    margin-right: 0;
  }
}

.preset {
  &-solid {
    background: #333;
    color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  &-sunset {
    background: linear-gradient(45deg, #ff6b6b, #feca57, #ff9ff3);
    color: white;
    box-shadow: 0 2px 8px rgba(255,107,107,0.3);
  }
  
  &-ocean {
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb);
    color: white;
    box-shadow: 0 2px 8px rgba(102,126,234,0.3);
  }
  
  &-fire {
    background: radial-gradient(circle, #ff9a9e, #fecfef);
    color: #333;
    box-shadow: 0 2px 8px rgba(255,154,158,0.3);
  }
  
  &-neon {
    background: linear-gradient(135deg, #00f5ff, #ff00ff, #ffff00);
    color: white;
    box-shadow: 0 2px 8px rgba(0,245,255,0.3);
  }
}

.text-preview {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(45deg, #f8f9fa 25%, transparent 25%), 
              linear-gradient(-45deg, #f8f9fa 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #f8f9fa 75%), 
              linear-gradient(-45deg, transparent 75%, #f8f9fa 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;
}

.css-output-container {
  position: relative;
  
  .copy-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  }
}

.css-output {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding-right: 40px;
}

.angle-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  
  .angle-arrow {
    font-size: 20px;
    font-weight: bold;
    color: #007bff;
    transition: transform 0.2s ease;
    transform-origin: center;
  }
}
</style>
